import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, parse } from 'date-fns';
import {
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  Award,
  Building2,
  IdCard,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Wallet,
  Clock,
  User,
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Check,
  Target,
  Activity,
  Sparkles,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useCandidateProfile } from 'src/services/api/queries/candidateQueries';
import SkillPill from 'src/components/common/SkillPill';
import {
  tierColorVar,
  tintFrom,
  TIERS,
} from 'src/pages/Candidate/SubmitCandidate/matchData';
import {
  useLocationMap,
  useStateMap,
} from 'src/services/api/queries/referenceDataQueries';
import { sortWorkExperiences } from 'src/lib/workExperience';

// ── Helpers ────────────────────────────────────────────────────────────────

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const normalizeYear = (y) => {
  const n = parseInt(y, 10);
  if (!n) return n;
  if (n < 100) return n <= 50 ? 2000 + n : 1900 + n;
  return n;
};

const formatDateRange = (dateString) => {
  if (!dateString) return '';
  try {
    const parts = String(dateString).split('/');
    if (parts.length === 2) {
      const mi = parseInt(parts[0], 10);
      const fullYear = normalizeYear(parts[1]);
      if (mi < 1 || mi > 12 || !fullYear) return dateString;
      return `${MONTHS_SHORT[mi - 1]} ${fullYear}`;
    }
    if (parts.length === 3) {
      const date = parse(
        `${parts[0]}/${parts[1]}/${parts[2]}`,
        'dd/MM/yyyy',
        new Date()
      );
      return format(date, 'MMM yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const parseWorkDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const parts = String(dateStr).split('/');
    const d = new Date(0);
    if (parts.length === 2) {
      const m = parseInt(parts[0], 10);
      const y = normalizeYear(parts[1]);
      if (!m || !y || m < 1 || m > 12) return null;
      d.setFullYear(y, m - 1, 1);
    } else if (parts.length === 3) {
      const m = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!m || !y || m < 1 || m > 12) return null;
      d.setFullYear(y, m - 1, parseInt(parts[0], 10) || 1);
    } else {
      return null;
    }
    return d;
  } catch {
    return null;
  }
};

const calcTenure = (fromDate, toDate, isCurrent) => {
  const start = parseWorkDate(fromDate);
  if (!start) return null;
  const end = isCurrent ? new Date() : parseWorkDate(toDate);
  if (!end) return null;
  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (months < 0 || months > 720) return null;
  if (months < 12) return `${months}m`;
  const yrs = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${yrs}y ${rem}m` : `${yrs}y`;
};

const formatCTC = (amount) => {
  if (!amount) return null;
  const n = Number(amount);
  if (Number.isNaN(n) || n === 0) return null;
  if (n >= 10000000) {
    const cr = n / 10000000;
    return `₹${cr % 1 === 0 ? cr : cr.toFixed(1)}Cr`;
  }
  if (n >= 100000) {
    const l = n / 100000;
    return `₹${l % 1 === 0 ? l : l.toFixed(1)}L`;
  }
  return `₹${n.toLocaleString('en-IN')}`;
};

const formatScore = (score) => {
  if (score == null || score === '') return '';
  return Number(score).toString();
};

const getQualDisplayName = (qt) => {
  const map = {
    TENTH: 'Secondary',
    TWELFTH: 'Higher Secondary',
    DEGREE: "Bachelor's Degree",
    MASTERS: "Master's Degree",
  };
  return map[qt] || qt;
};

// ── Modal Chrome CSS ────────────────────────────────────────────────────────

const CP_CSS = `
.cp-scrim {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: 0; pointer-events: none;
  transition: opacity .22s ease;
}
.cp-scrim.cp-open { opacity: 1; pointer-events: all; }

.cp-modal {
  position: fixed; inset: 18px;
  max-width: 1400px; margin: 0 auto;
  background: hsl(var(--background));
  border-radius: 14px;
  box-shadow: 0 24px 80px rgba(0,0,0,.22), 0 0 0 1px rgba(0,0,0,.06);
  z-index: 1001;
  display: none; flex-direction: column;
  overflow: hidden;
  opacity: 0; transform: translateY(8px) scale(0.993);
  transition: opacity .22s ease, transform .22s cubic-bezier(.4,0,.2,1);
}
.cp-modal.cp-mounted { display: flex; }
.cp-modal.cp-open { opacity: 1; transform: none; }

.cp-load {
  position: absolute; top: 0; left: 0; right: 0;
  height: 2.5px; overflow: hidden; z-index: 10;
  opacity: 0; transition: opacity .2s; pointer-events: none;
  background: hsl(var(--border));
}
.cp-load.cp-on { opacity: 1; }
.cp-load::after {
  content: ''; position: absolute;
  left: -65%; width: 65%; height: 100%;
  background: linear-gradient(90deg,transparent,hsl(var(--primary)) 40%,hsl(var(--primary)) 60%,transparent);
  animation: cp-sweep 1.5s ease-in-out infinite;
}
@keyframes cp-sweep { from { left: -65%; } to { left: 110%; } }

.cp-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 16px; flex-shrink: 0;
  border-bottom: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  position: relative;
}
.cp-bar-av {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg,hsl(var(--primary)),var(--brand-deep));
  color: hsl(var(--primary-foreground)); font-size: 13px; font-weight: 700; letter-spacing: -.5px;
  display: flex; align-items: center; justify-content: center;
  user-select: none;
}
.cp-bar-txt { flex: 1; min-width: 0; }
.cp-bar-name {
  font-size: 15px; font-weight: 700; letter-spacing: -.025em;
  color: hsl(var(--foreground));
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cp-bar-sub {
  font-size: 12px; color: hsl(var(--muted-foreground));
  margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cp-bar-score {
  display: inline-flex; align-items: baseline; gap: 2px;
  padding: 4px 10px; border-radius: 8px; flex-shrink: 0;
}
.cp-bar-score.cp-best { background: var(--ds-success-subtle); border: 1px solid var(--ds-success-tint); }
.cp-bar-score.cp-near { background: var(--ds-warning-subtle); border: 1px solid var(--ds-warning-tint); }
.cp-bar-score.cp-explore { background: hsl(var(--muted)); border: 1px solid hsl(var(--border)); }
.cp-score-num { font-size: 14px; font-weight: 700; line-height: 1; }
.cp-score-denom { font-size: 11px; font-weight: 500; opacity: .6; line-height: 1; }
.cp-bar-score.cp-best .cp-score-num,
.cp-bar-score.cp-best .cp-score-denom { color: var(--ds-success); }
.cp-bar-score.cp-near .cp-score-num,
.cp-bar-score.cp-near .cp-score-denom { color: var(--ds-warning); }
.cp-bar-score.cp-explore .cp-score-num,
.cp-bar-score.cp-explore .cp-score-denom { color: hsl(var(--muted-foreground)); }

.cp-pager { display: flex; align-items: center; gap: 0; flex-shrink: 0;
  border: 1px solid hsl(var(--border)); border-radius: 8px; overflow: hidden; }
.cp-pg {
  width: 30px; height: 30px; flex-shrink: 0;
  border: none; background: hsl(var(--card));
  color: hsl(var(--muted-foreground));
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all .15s;
}
.cp-pg:hover:not([disabled]) {
  background: hsl(var(--brand-bg-subtle)); color: hsl(var(--primary));
}
.cp-pg[disabled] { opacity: .35; cursor: default; }
.cp-pg-count {
  font-size: 12px; font-weight: 500; color: hsl(var(--foreground));
  padding: 0 10px; white-space: nowrap;
  border-left: 1px solid hsl(var(--border)); border-right: 1px solid hsl(var(--border));
  height: 30px; display: flex; align-items: center;
}

.cp-btn-ghost {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px; border-radius: 7px; flex-shrink: 0;
  border: 1px solid hsl(var(--border)); background: hsl(var(--card));
  color: hsl(var(--muted-foreground));
  font-size: 12px; font-weight: 550; cursor: pointer;
  text-decoration: none; transition: all .15s; white-space: nowrap;
  font-family: inherit;
}
.cp-btn-ghost:hover { border-color: hsl(var(--primary)); color: hsl(var(--primary)); background: hsl(var(--brand-bg-subtle)); }
.cp-btn-ghost:disabled { opacity: .45; cursor: default; pointer-events: none; }

.cp-close {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 8px 5px 11px; border-radius: 7px; flex-shrink: 0;
  border: 1px solid hsl(var(--border)); background: hsl(var(--card));
  color: hsl(var(--muted-foreground));
  font-size: 12px; font-weight: 550; cursor: pointer;
  transition: all .15s; font-family: inherit; white-space: nowrap;
}
.cp-close:hover { border-color: var(--ds-danger); color: var(--ds-danger); background: var(--ds-danger-subtle); }
.cp-close:hover .cp-esc-badge { border-color: var(--ds-danger); background: var(--ds-danger-subtle); }
.cp-esc-badge {
  padding: 2px 5px; border-radius: 4px; flex-shrink: 0;
  background: hsl(var(--muted)); border: 1px solid hsl(var(--border));
  color: hsl(var(--muted-foreground));
  font-size: 10px; font-weight: 600; font-family: inherit;
  cursor: default; user-select: none; line-height: 1.4;
}

.cp-subnav {
  display: flex; align-items: center; gap: 2px;
  padding: 0 16px; flex-shrink: 0; overflow-x: auto;
  border-bottom: 1px solid hsl(var(--border));
  background: hsl(var(--muted));
  overflow-y: hidden; scrollbar-width: thin; scrollbar-color: hsl(var(--border)) transparent;
}
.cp-nav-btn {
  padding: 10px 13px; font-size: 12.5px; font-weight: 550;
  border: none; border-bottom: 2px solid transparent; margin-bottom: -1px;
  background: transparent; cursor: pointer;
  transition: color .15s, border-color .15s;
  color: hsl(var(--muted-foreground)); white-space: nowrap;
  font-family: inherit;
}
.cp-nav-btn:hover { color: hsl(var(--foreground)); }
.cp-nav-btn.cp-active { color: hsl(var(--primary)); border-bottom-color: hsl(var(--primary)); }

.cp-subnav-hint {
  margin-left: auto; font-size: 11px; color: hsl(var(--muted-foreground));
  opacity: .55; white-space: nowrap; padding-left: 16px; flex-shrink: 0;
}

.cp-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; }

.cp-body { max-width: 1180px; margin: 0 auto; padding: 24px 20px 80px; }

.cp-foot {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px; flex-shrink: 0;
  border-top: 1px solid hsl(var(--border)); background: hsl(var(--card));
}
.cp-foot-meta { flex: 1; min-width: 0; font-size: 12px; color: hsl(var(--muted-foreground)); }
.cp-foot-actions { display: flex; align-items: center; gap: 8px; }

.cp-foot-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 7px;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  transition: all .15s; border: 1px solid hsl(var(--border));
  flex-shrink: 0; white-space: nowrap; font-family: inherit;
}
.cp-foot-btn.cp-ghost {
  background: hsl(var(--card)); border-color: hsl(var(--border));
  color: hsl(var(--muted-foreground));
}
.cp-foot-btn.cp-ghost:hover { border-color: hsl(var(--primary)); color: hsl(var(--primary)); background: hsl(var(--brand-bg-subtle)); }
.cp-foot-btn.cp-shortlist { background: hsl(var(--brand-bg-subtle)); border-color: rgba(var(--brand-blue-rgb),.3); color: hsl(var(--primary)); }
.cp-foot-btn.cp-shortlist:hover { background: hsl(var(--primary)); color: hsl(var(--primary-foreground)); border-color: hsl(var(--primary)); }
.cp-foot-btn.cp-shortlisted { background: var(--ds-success-subtle); border-color: var(--ds-success-tint); color: var(--ds-success); }
.cp-foot-btn.cp-shortlisted:hover { background: var(--ds-danger-subtle); border-color: var(--ds-danger); color: var(--ds-danger); }

/* ── Match evidence card ─────────────────────────────────────────────────── */
.cp-match {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--card));
  overflow: hidden;
  margin-bottom: 20px;
}
.cp-match-head {
  display: flex; align-items: center; gap: 9px;
  padding: 13px 18px; border-bottom: 1px solid hsl(var(--border));
}
.cp-mh-ic {
  width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.cp-match-head h2 {
  font-size: 14px; font-weight: 700; letter-spacing: -.02em;
  color: hsl(var(--foreground)); margin: 0;
}
.cp-match-rank {
  margin-left: auto; flex-shrink: 0;
  font-size: 11px; font-weight: 600; letter-spacing: .02em;
  padding: 3px 10px; border-radius: 20px; white-space: nowrap;
}
.cp-mc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
@media (max-width: 1080px) { .cp-mc-grid { grid-template-columns: 1fr; } }
.cp-mc-col { padding: 16px 18px; }
.cp-mc-col + .cp-mc-col { border-left: 1px solid hsl(var(--border)); }
@media (max-width: 1080px) {
  .cp-mc-col + .cp-mc-col { border-left: none; border-top: 1px solid hsl(var(--border)); }
}
.cp-mc-lab {
  display: flex; align-items: center; gap: 6px;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .07em; color: hsl(var(--muted-foreground)); margin-bottom: 14px;
}
.cp-mc-m {
  margin-left: auto; font-size: 10.5px; font-weight: 600;
  font-family: ui-monospace, 'SF Mono', 'Consolas', monospace;
  text-transform: none; letter-spacing: 0;
}
.cp-mbar-row {
  display: grid; grid-template-columns: 104px 1fr 30px;
  align-items: center; gap: 10px; margin-bottom: 10px;
}
.cp-mbar-row:last-child { margin-bottom: 0; }
.cp-mbar-lbl {
  font-size: 11.5px; font-weight: 500; color: hsl(var(--muted-foreground));
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cp-mbar-track { height: 8px; border-radius: 5px; overflow: hidden; background: hsl(var(--border)); }
.cp-mbar-fill { display: block; height: 100%; border-radius: 5px; transition: width .6s cubic-bezier(.4,0,.2,1); }
.cp-mbar-val {
  font-size: 12.5px; font-weight: 700; text-align: right;
  font-variant-numeric: tabular-nums; color: hsl(var(--foreground));
  font-family: ui-monospace, 'SF Mono', 'Consolas', monospace;
}
.cp-fits { display: flex; flex-wrap: wrap; gap: 8px 14px; margin-top: 14px; }
.cp-fit-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 500; }
.cp-fit-pill.cp-pass { color: hsl(var(--muted-foreground)); }
.cp-fit-pill.cp-warn { color: var(--ds-warning); }
.cp-fit-pill.cp-fail { color: var(--ds-danger); }
.cp-skillmap { display: flex; flex-wrap: wrap; gap: 6px; }
.cp-skill-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 5px; font-size: 11.5px; font-weight: 550;
  border: 1px solid transparent;
}
.cp-skill-tag.cp-matched { background: var(--ds-success-subtle); color: var(--ds-success); border-color: var(--ds-success-tint); }
.cp-skill-tag.cp-partial { background: var(--ds-warning-subtle); color: var(--ds-warning); border-color: var(--ds-warning-tint); }
.cp-skill-tag.cp-missing { background: hsl(var(--muted)); color: hsl(var(--muted-foreground)); border-color: hsl(var(--border)); }
.cp-req-tag { font-size: 8.5px; font-weight: 700; letter-spacing: .04em; opacity: .7; margin-left: 1px; }
.cp-ai {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 14px 18px; border-top: 1px solid hsl(var(--border));
}
.cp-ai-ic {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.cp-ai-lab { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 3px; }
.cp-ai-txt { font-size: 12.5px; line-height: 1.6; color: hsl(var(--foreground)); margin: 0; }

`;

let cssInjected = false;

// ── Component ───────────────────────────────────────────────────────────────

const CandidateProfileModal = ({
  isOpen,
  onClose,
  candidateId,
  opportunityId,
  candidates,
  currentIndex,
  onNavigate,
  candidateScore,
  candidateTier,
  isSelected,
  onToggleSelect,
  selectDisabled,
  // Extra query params merged onto the "Full page" link below, alongside
  // opportunityId — e.g. AssignmentPipeline.jsx passes the row's own status
  // plus assignmentId/requirementLocationId so Preview.jsx can still build
  // its (default) breadcrumb link with full context. Note this can't carry
  // the `state: { from: 'assignment-pipeline' }` flag a same-tab navigate()
  // could — the link below is a real target="_blank" anchor, a fresh
  // browser tab with no router state to inherit — so that specific
  // breadcrumb still falls back to Preview's generic Candidates link when
  // reached this way. See RowActionsCell.jsx for the same navigate this
  // replaced.
  fullPageQuery = {},
}) => {
  const locationMap = useLocationMap();
  const stateMap = useStateMap();
  const { toast } = useToast();

  const [mounted, setMounted] = useState(false);
  const [openCls, setOpenCls] = useState(false);
  const { data: profileResponse, isLoading } = useCandidateProfile(
    candidateId,
    opportunityId,
    { enabled: !!candidateId && isOpen }
  );
  const candidateData = profileResponse?.success
    ? profileResponse.data.profile_data
    : null;
  const regId = profileResponse?.success
    ? profileResponse.data.registration_id
    : null;
  const [activeSection, setActiveSection] = useState('match');
  const [expandedDuties, setExpandedDuties] = useState(new Set());
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});

  // Inject modal CSS once globally
  useEffect(() => {
    if (cssInjected) return;
    const style = document.createElement('style');
    style.id = 'cp-modal-styles';
    style.textContent = CP_CSS;
    document.head.appendChild(style);
    cssInjected = true;
  }, []);

  // Mount / open animation + body scroll lock
  useEffect(() => {
    let t;
    if (isOpen) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
      t = setTimeout(() => setOpenCls(true), 10);
    } else {
      setOpenCls(false);
      t = setTimeout(() => {
        setMounted(false);
        document.body.style.overflow = '';
      }, 250);
    }
    return () => clearTimeout(t);
  }, [isOpen]);

  // Reset the view (scroll, expanded duties, active section) whenever a new
  // candidate is opened. The profile data itself comes from useCandidateProfile.
  useEffect(() => {
    if (candidateId && isOpen) {
      setExpandedDuties(new Set());
      setActiveSection('match');
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }
  }, [candidateId, isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')
        return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0 && onNavigate) {
        onNavigate(currentIndex - 1);
      } else if (
        e.key === 'ArrowRight' &&
        currentIndex < (candidates?.length ?? 0) - 1 &&
        onNavigate
      ) {
        onNavigate(currentIndex + 1);
      } else if (e.key === 's' || e.key === 'S') {
        if (onToggleSelect && candidateId && (isSelected || !selectDisabled)) {
          onToggleSelect(candidateId);
        }
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, onNavigate, currentIndex, candidates?.length]);

  // Scroll-spy for active section
  useEffect(() => {
    if (!candidateData || !scrollRef.current) return undefined;

    const SECTIONS = [
      'match',
      'experience',
      'skills',
      'education',
      'certifications',
    ];
    const observers = SECTIONS.map((section) => {
      const el = sectionRefs.current[section];
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(section);
        },
        {
          root: scrollRef.current,
          rootMargin: '-20% 0px -55% 0px',
          threshold: 0,
        }
      );
      obs.observe(el);
      return obs;
    });

    return () => observers.forEach((obs) => obs && obs.disconnect());
  }, [candidateData]);

  const handleCopyRegId = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(String(regId ?? ''));
        toast({
          message: 'Registration ID copied',
          variant: 'success',
          duration: 1500,
        });
      } catch {
        toast({
          message: 'Copy failed',
          variant: 'destructive',
          duration: 1500,
        });
      }
    },
    [regId, toast]
  );

  const toggleDuties = useCallback((key) => {
    setExpandedDuties((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const scrollToSection = useCallback((section) => {
    setActiveSection(section);
    const el = sectionRefs.current[section];
    if (!el || !scrollRef.current) return;
    const containerTop = scrollRef.current.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;
    const offset = scrollRef.current.scrollTop + (elTop - containerTop) - 16;
    scrollRef.current.scrollTo({ top: offset, behavior: 'smooth' });
  }, []);

  if (!mounted) return null;

  // ── Header-level derived values ──────────────────────────────────────────
  const pi = candidateData?.personal_information;
  const sortedWorkH = sortWorkExperiences(candidateData?.work_experiences);
  const currentExpH = sortedWorkH.find((e) => e.is_current) || sortedWorkH[0];

  const headerName = pi
    ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim()
    : candidates?.[currentIndex]?.name || '';

  const headerInitials = headerName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const headerSub = [currentExpH?.role, currentExpH?.company_name]
    .filter(Boolean)
    .join(' at ');

  const hasPrev = (candidates?.length ?? 0) > 1 && currentIndex > 0;
  const hasNext =
    (candidates?.length ?? 0) > 1 &&
    currentIndex < (candidates?.length ?? 0) - 1;

  let tierCls = 'cp-explore';
  if (candidateTier === 'best') tierCls = 'cp-best';
  else if (candidateTier === 'near') tierCls = 'cp-near';

  // "Full page" link's href — opportunityId was already always included;
  // fullPageQuery layers on whatever extra context the caller has (see the
  // prop comment above).
  const fullPageParams = new URLSearchParams();
  if (opportunityId) fullPageParams.set('opportunityId', opportunityId);
  Object.entries(fullPageQuery).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      fullPageParams.set(key, value);
    }
  });
  const fullPageQs = fullPageParams.toString();
  const fullPageHref = `/candidates/${candidateId}${fullPageQs ? `?${fullPageQs}` : ''}`;

  // ── Match evidence card (sourced from the list view-model) ────────────────
  const renderMatchCard = () => {
    const vm = candidates?.[currentIndex];
    if (!vm) return null;

    const bars = (vm.score_breakdown || []).filter(
      (s) => s.available && s.value != null
    );
    const skillMap = vm.skill_map || [];
    const fits = vm.fit_flags || [];

    // Nothing meaningful to show
    if (!bars.length && !skillMap.length && !fits.length && !vm.insight) {
      return null;
    }

    const accent = tierColorVar(vm.tier);
    const tierLabel = TIERS[vm.tier]?.label || vm.tier || '';
    const scoreVal = Math.round(vm.score ?? candidateScore ?? 0);
    const total = candidates?.length ?? 0;
    const rankNum = parseInt(vm.rank, 10) || currentIndex + 1;

    const covMatched =
      vm.coverage?.matched ??
      skillMap.filter((s) => s.status === 'matched').length;
    const covPartial =
      vm.coverage?.partial ??
      skillMap.filter((s) => s.status === 'partial').length;
    const covGaps =
      vm.coverage?.missing ??
      skillMap.filter((s) => s.status === 'missing').length;

    const fitState = (passed) => {
      if (passed === true) return 'cp-pass';
      if (passed === false) return 'cp-fail';
      return 'cp-warn';
    };
    const fitIcon = (passed) => {
      if (passed === true) return <Check size={11} strokeWidth={2.2} />;
      if (passed === false) return <X size={11} strokeWidth={2.2} />;
      return <AlertTriangle size={11} strokeWidth={2.2} />;
    };
    const skillIcon = (status) => {
      if (status === 'matched') return <Check size={10} strokeWidth={2.5} />;
      if (status === 'missing') return <X size={10} strokeWidth={2.5} />;
      return <span aria-hidden="true">~</span>;
    };

    return (
      <section
        className="cp-match"
        id="cp-section-match"
        ref={(el) => {
          sectionRefs.current.match = el;
        }}
      >
        <div className="cp-match-head">
          <span
            className="cp-mh-ic"
            style={{ background: tintFrom(accent, 16), color: accent }}
          >
            <Target size={14} strokeWidth={2} />
          </span>
          <h2>Why this candidate ranked here</h2>
          <span
            className="cp-match-rank"
            style={{ background: tintFrom(accent, 14), color: accent }}
          >
            #{rankNum}
            {total ? ` of ${total}` : ''}
            {tierLabel ? ` · ${tierLabel}` : ''}
          </span>
        </div>

        <div className="cp-mc-grid">
          {/* Score breakdown */}
          <div className="cp-mc-col">
            <div className="cp-mc-lab">
              <Activity size={12} strokeWidth={2} />
              Score breakdown
              <span className="cp-mc-m" style={{ color: accent }}>
                {scoreVal} / 100
              </span>
            </div>
            {bars.length > 0 ? (
              bars.map((s) => {
                const v = Math.round(s.value);
                return (
                  <div className="cp-mbar-row" key={s.key}>
                    <span className="cp-mbar-lbl">{s.label}</span>
                    <span className="cp-mbar-track">
                      <span
                        className="cp-mbar-fill"
                        style={{ width: `${v}%`, background: accent }}
                      />
                    </span>
                    <span className="cp-mbar-val">{v}</span>
                  </div>
                );
              })
            ) : (
              <span className="text-[11.5px] text-muted-foreground">
                No score breakdown available.
              </span>
            )}
            {fits.length > 0 && (
              <div className="cp-fits">
                {fits.map((f) => (
                  <span
                    key={f.field}
                    className={`cp-fit-pill ${fitState(f.passed)}`}
                  >
                    {fitIcon(f.passed)}
                    {f.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Skill map vs JD */}
          <div className="cp-mc-col">
            <div className="cp-mc-lab">
              <Target size={12} strokeWidth={2} />
              Skill map vs JD
              <span className="cp-mc-m">
                {covMatched} matched · {covPartial} partial · {covGaps} gaps
              </span>
            </div>
            {skillMap.length > 0 ? (
              <div className="cp-skillmap">
                {skillMap.map((s) => (
                  <span
                    key={s.name}
                    className={`cp-skill-tag cp-${s.status || 'missing'}`}
                  >
                    {skillIcon(s.status)}
                    {s.name}
                    {s.required && <span className="cp-req-tag">REQ</span>}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-[11.5px] text-muted-foreground">
                No JD skills defined.
              </span>
            )}
          </div>
        </div>

        {vm.insight && (
          <div className="cp-ai">
            <span
              className="cp-ai-ic"
              style={{ background: tintFrom(accent, 16), color: accent }}
            >
              <Sparkles size={16} strokeWidth={1.8} />
            </span>
            <div>
              <div className="cp-ai-lab" style={{ color: accent }}>
                AI match analysis
              </div>
              <p className="cp-ai-txt">{vm.insight}</p>
            </div>
          </div>
        )}
      </section>
    );
  };

  // ── Profile body ─────────────────────────────────────────────────────────
  const renderBody = () => {
    if (isLoading) {
      return (
        <div
          className="flex items-center justify-center gap-2 text-muted-foreground"
          style={{ minHeight: 300 }}
        >
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-[13px]">Loading profile…</span>
        </div>
      );
    }

    if (!candidateData) return null;

    const {
      personal_information: personalInfo,
      educational_details: educationalDetails,
      work_experiences: rawWorkExperiences,
      skills,
      certifications,
    } = candidateData;

    const workExperiences = sortWorkExperiences(rawWorkExperiences);

    const currentExp =
      workExperiences.find((e) => e.is_current) || workExperiences[0];

    const heroInitials = `${personalInfo.first_name?.[0] || ''}${
      personalInfo.last_name?.[0] || ''
    }`.toUpperCase();

    const heroFullName = [personalInfo.first_name, personalInfo.last_name]
      .filter(Boolean)
      .join(' ');

    const heroLocation = [
      personalInfo.address?.city_name ||
        (personalInfo.address?.city
          ? locationMap[personalInfo.address.city] || personalInfo.address.city
          : null),
      personalInfo.address?.state_name ||
        (personalInfo.address?.state
          ? stateMap[personalInfo.address.state] || personalInfo.address.state
          : null),
    ]
      .filter(Boolean)
      .join(', ');

    const od = candidateData?.other_details || {};
    const totalExpMonths = od.total_work_experience;
    const totalExpLabel = (() => {
      if (!totalExpMonths) return null;
      const y = Math.floor(totalExpMonths / 12);
      const m = totalExpMonths % 12;
      const parts = [];
      if (y) parts.push(`${y} yr${y > 1 ? 's' : ''}`);
      if (m) parts.push(`${m} mo`);
      return parts.join(' ') || null;
    })();

    const skillsCount = (skills || []).length;
    const domainSkills = (skills || []).filter(
      (s) => !s.type || s.type === 'DOMAIN_SKILL'
    );
    const toolSkills = (skills || []).filter((s) => s.type === 'TOOL');
    const regSkills = (skills || []).filter((s) => s.type === 'REGULATORY');
    const hasSkillTypes = (skills || []).some((s) => s.type);

    const latestEdu = educationalDetails?.length
      ? [...educationalDetails].sort(
          (a, b) => (b.graduation_year || 0) - (a.graduation_year || 0)
        )[0]
      : null;

    const snapshotRows = [
      currentExp?.company_name
        ? {
            icon: Building2,
            label: 'Current employer',
            value: currentExp.company_name,
          }
        : null,
      currentExp?.role
        ? { icon: Briefcase, label: 'Current role', value: currentExp.role }
        : null,
      latestEdu
        ? {
            icon: GraduationCap,
            label: 'Highest qualification',
            value: getQualDisplayName(latestEdu.qualification_type),
            sub: latestEdu.graduation_year
              ? `· ${latestEdu.graduation_year}`
              : null,
          }
        : null,
      personalInfo.notice_period != null
        ? {
            icon: Clock,
            label: 'Notice period',
            value: `${personalInfo.notice_period} days`,
          }
        : null,
      formatCTC(personalInfo.current_ctc)
        ? {
            icon: Wallet,
            label: 'Current CTC',
            value: formatCTC(personalInfo.current_ctc),
          }
        : null,
      formatCTC(personalInfo.expected_ctc)
        ? {
            icon: TrendingUp,
            label: 'Expected CTC',
            value: formatCTC(personalInfo.expected_ctc),
          }
        : null,
      personalInfo.preferred_work_locations?.length > 0
        ? {
            icon: MapPin,
            label: 'Preferred Locations',
            value: personalInfo.preferred_work_locations
              .map((locId) => locationMap[locId] || locId)
              .join(', '),
          }
        : null,
      personalInfo.email
        ? { icon: Mail, label: 'Email', value: personalInfo.email }
        : null,
      personalInfo.phone
        ? { icon: Phone, label: 'Phone', value: personalInfo.phone }
        : null,
    ].filter(Boolean);

    return (
      <div className="cp-body">
        {/* ── Hero card ─────────────────────────────────────────────────── */}
        <div className="bg-card border border-border rounded-xl overflow-hidden mb-5">
          <div
            style={{
              height: 52,
              background:
                'linear-gradient(178deg,#1B6AAF 0%,#155A94 35%,#0E4170 70%,#0A3058 100%)',
            }}
            className="relative overflow-hidden"
          >
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 60% 50%,rgba(255,255,255,.08) 0%,transparent 70%)',
              }}
            />
          </div>
          <div className="flex gap-4 items-start px-5 pb-4">
            <div
              style={{
                width: 76,
                height: 76,
                marginTop: -30,
                background: 'linear-gradient(135deg,#2068B2,#1553A0)',
                fontSize: 20,
                letterSpacing: -1,
              }}
              className="rounded-[18px] border-[3px] border-card shadow-[0_4px_14px_rgba(0,0,0,.14)] flex-shrink-0 flex items-center justify-center text-white font-bold select-none z-[2]"
            >
              {heroInitials}
            </div>
            <div className="flex-1 min-w-0 pt-3">
              <div className="text-[20px] font-bold tracking-[-0.03em] text-foreground leading-tight">
                {heroFullName}
              </div>
              {(currentExp?.role || currentExp?.company_name) && (
                <div className="text-[13.5px] text-muted-foreground mt-1">
                  {currentExp?.role}
                  {currentExp?.role && currentExp?.company_name && (
                    <span className="mx-1.5 opacity-50">&middot;</span>
                  )}
                  {currentExp?.company_name}
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-2.5">
                {heroLocation && (
                  <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                    <MapPin size={12} className="opacity-60" />
                    {heroLocation}
                  </span>
                )}
                {totalExpLabel && (
                  <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                    <Briefcase size={12} className="opacity-60" />
                    {totalExpLabel} exp
                  </span>
                )}
                {personalInfo.email && (
                  <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                    <Mail size={12} className="opacity-60" />
                    {personalInfo.email}
                  </span>
                )}
                {personalInfo.phone && (
                  <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                    <Phone size={12} className="opacity-60" />
                    {personalInfo.phone}
                  </span>
                )}
              </div>
            </div>
            {regId && (
              <div className="pt-3 flex-shrink-0">
                <span className="group inline-flex items-center gap-1.5 rounded-full bg-brand-bg-subtle border border-primary/15 px-3 py-1.5 text-[11.5px] font-semibold text-primary">
                  <IdCard size={12} />
                  {regId}
                  <button
                    type="button"
                    onClick={handleCopyRegId}
                    aria-label="Copy registration ID"
                    className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 -mr-0.5 hover:bg-primary/15"
                  >
                    <Copy size={11} />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        {renderMatchCard()}

        {/* ── Two-column grid ───────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) 296px',
            gap: 18,
            alignItems: 'start',
          }}
        >
          {/* Main column */}
          <div className="flex flex-col gap-4">
            {/* Experience */}
            <div
              ref={(el) => {
                sectionRefs.current.experience = el;
              }}
              id="cp-section-experience"
              className="bg-card border border-border rounded-xl p-[18px_20px]"
            >
              <div className="flex items-center gap-2 mb-3.5">
                <div className="w-6 h-6 rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                  <Briefcase size={12} />
                </div>
                <h2 className="text-[15px] font-bold tracking-[-0.02em] text-foreground m-0">
                  Experience
                </h2>
                {workExperiences?.length > 0 && (
                  <span className="font-mono text-[10.5px] font-semibold text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded-full ml-auto">
                    {workExperiences.length}
                  </span>
                )}
              </div>

              {workExperiences?.length > 0 ? (
                <div>
                  {workExperiences.map((exp, idx) => {
                    const isCurrent = !!exp.is_current;
                    const tenure = calcTenure(
                      exp.from_date,
                      exp.to_date,
                      isCurrent
                    );
                    const rawDuties = exp.duties || [];
                    const sortedDuties = [...rawDuties].sort((a, b) => {
                      if (a.type === 'ACHIEVEMENT' && b.type !== 'ACHIEVEMENT')
                        return -1;
                      if (b.type === 'ACHIEVEMENT' && a.type !== 'ACHIEVEMENT')
                        return 1;
                      return 0;
                    });
                    const expKey = `exp-${idx}-${exp.company_name || idx}`;
                    const isExpanded = expandedDuties.has(expKey);
                    const visibleDuties = isExpanded
                      ? sortedDuties
                      : sortedDuties.slice(0, 2);
                    const hiddenCount = sortedDuties.length - 2;
                    const toDateLabel = (() => {
                      if (isCurrent) return 'Present';
                      if (exp.to_date) return formatDateRange(exp.to_date);
                      return '—';
                    })();

                    return (
                      <div key={expKey} className="relative pb-5 pl-7">
                        {idx < workExperiences.length - 1 && (
                          <div className="absolute left-[6px] top-4 bottom-[-2px] w-[2px] bg-border" />
                        )}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 4,
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            border: isCurrent
                              ? '3px solid #2068B2'
                              : '3px solid hsl(var(--muted-foreground) / 0.5)',
                            boxShadow: isCurrent
                              ? '0 0 0 3px rgba(32,104,178,.12)'
                              : '0 0 0 3px hsl(var(--muted))',
                          }}
                          className="bg-card"
                        />
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                          <div>
                            <div className="text-[13.5px] font-[650] text-foreground leading-[1.3]">
                              {exp.role || '—'}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[12px] text-muted-foreground flex-wrap">
                              <Building2 size={11} className="opacity-60" />
                              {exp.company_name}
                              {isCurrent && (
                                <span className="text-[9px] font-bold text-primary bg-brand-bg-subtle border border-primary/15 px-1.5 py-0.5 rounded-full uppercase tracking-[0.04em]">
                                  Current
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 text-[11.5px] text-muted-foreground">
                            <div>
                              {formatDateRange(exp.from_date)}
                              &nbsp;&ndash;&nbsp;
                              {toDateLabel}
                            </div>
                            {tenure && (
                              <div className="text-[11px] mt-0.5 font-semibold">
                                {tenure}
                              </div>
                            )}
                          </div>
                        </div>

                        {exp.skills?.length > 0 &&
                          (() => {
                            const skillsKey = `${expKey}-skills`;
                            const skillsExpanded =
                              expandedDuties.has(skillsKey);
                            const SKILL_LIMIT = 4;
                            const visibleSkills = skillsExpanded
                              ? exp.skills
                              : exp.skills.slice(0, SKILL_LIMIT);
                            const hiddenSkillCount =
                              exp.skills.length - SKILL_LIMIT;
                            return (
                              <div className="mb-2.5 flex flex-wrap gap-1.5 items-center">
                                {visibleSkills.map((skill, skillIdx) => (
                                  <SkillPill
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${expKey}-skill-${skillIdx}-${skill}`}
                                    label={skill}
                                  />
                                ))}
                                {!skillsExpanded && hiddenSkillCount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => toggleDuties(skillsKey)}
                                    className="text-[11.5px] font-semibold text-muted-foreground bg-muted border border-border rounded-[5px] px-2.5 py-1 cursor-pointer"
                                  >
                                    +{hiddenSkillCount} more
                                  </button>
                                )}
                                {skillsExpanded && hiddenSkillCount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => toggleDuties(skillsKey)}
                                    className="text-[11.5px] font-semibold text-muted-foreground bg-transparent border-none px-0.5 py-1 cursor-pointer"
                                  >
                                    show less
                                  </button>
                                )}
                              </div>
                            );
                          })()}

                        {visibleDuties.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            {visibleDuties.map((duty) => {
                              const isAch = duty.type === 'ACHIEVEMENT';
                              const dutyKey = `${duty.type || 'r'}-${(
                                duty.description || ''
                              ).slice(0, 30)}`;
                              return (
                                <div
                                  key={dutyKey}
                                  className={`rounded-[7px] px-3 py-2.5 ${
                                    isAch
                                      ? 'bg-ds-success-subtle border border-ds-success/20'
                                      : 'bg-card border border-border'
                                  }`}
                                >
                                  <div
                                    className="flex items-center gap-1.5 flex-wrap"
                                    style={{
                                      marginBottom: duty.description ? 3 : 0,
                                    }}
                                  >
                                    {isAch && (
                                      <span className="text-[9px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded-[3px] bg-ds-success text-white flex-shrink-0">
                                        Achievement
                                      </span>
                                    )}
                                    {duty.title && (
                                      <span className="text-[12.5px] font-[650] text-foreground">
                                        {duty.title}
                                      </span>
                                    )}
                                    {isAch && duty.impact && (
                                      <span className="inline-flex items-center gap-0.5 text-[11px] font-[650] text-ds-success mr-auto">
                                        <TrendingUp size={10} />
                                        {duty.impact}
                                      </span>
                                    )}
                                  </div>
                                  {duty.description && (
                                    <p className="text-[12.5px] m-0 leading-[1.55] text-muted-foreground">
                                      {duty.description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                            {sortedDuties.length > 2 && (
                              <button
                                type="button"
                                onClick={() => toggleDuties(expKey)}
                                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary bg-transparent border-none cursor-pointer py-1 self-start"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp size={12} /> Show less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={12} /> Show {hiddenCount}{' '}
                                    more
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12.5px] text-muted-foreground text-center py-4 m-0">
                  No work experience added yet
                </p>
              )}
            </div>

            {/* Education */}
            <div
              ref={(el) => {
                sectionRefs.current.education = el;
              }}
              id="cp-section-education"
              className="bg-card border border-border rounded-xl p-[18px_20px]"
            >
              <div className="flex items-center gap-2 mb-3.5">
                <div className="w-6 h-6 rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={12} />
                </div>
                <h2 className="text-[15px] font-bold tracking-[-0.02em] text-foreground m-0">
                  Education
                </h2>
                {educationalDetails?.length > 0 && (
                  <span className="font-mono text-[10.5px] font-semibold text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded-full ml-auto">
                    {educationalDetails.length}
                  </span>
                )}
              </div>
              {educationalDetails?.length > 0 ? (
                <div>
                  {[...educationalDetails]
                    .sort(
                      (a, b) =>
                        (b.graduation_year || 0) - (a.graduation_year || 0)
                    )
                    .map((edu) => {
                      const eduKey = `${edu.institution}-${edu.graduation_year}-${edu.qualification_type}`;
                      return (
                        <div
                          key={eduKey}
                          className="flex gap-2.5 py-3 border-b border-border last:border-b-0"
                        >
                          <div className="w-9 h-9 rounded-[9px] bg-brand-bg-subtle text-primary border border-border flex items-center justify-center flex-shrink-0">
                            <GraduationCap size={15} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.06em] text-primary">
                              {getQualDisplayName(edu.qualification_type)}
                            </div>
                            <div className="text-[13px] font-[650] text-foreground mt-0.5">
                              {edu.institution || '—'}
                            </div>
                            {edu.field_of_study_name && (
                              <div className="text-[12px] text-muted-foreground mt-0.5">
                                {edu.field_of_study_name}
                              </div>
                            )}
                            <div className="text-[11.5px] text-muted-foreground mt-0.5 flex gap-2.5 flex-wrap">
                              {edu.graduation_year && (
                                <span>{edu.graduation_year}</span>
                              )}
                              {edu.board_type && <span>{edu.board_type}</span>}
                              {edu.score_percentage != null &&
                                edu.score_percentage !== '' && (
                                  <span className="font-semibold text-foreground">
                                    {formatScore(edu.score_percentage)}%
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-[12.5px] text-muted-foreground text-center py-4 m-0">
                  No education added yet
                </p>
              )}
            </div>
          </div>

          {/* Rail */}
          <div className="flex flex-col gap-4">
            {/* Snapshot */}
            <div className="bg-card border border-border rounded-xl p-[16px_18px]">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                  <User size={12} />
                </div>
                <h3 className="text-[13.5px] font-bold tracking-[-0.02em] text-foreground m-0">
                  Snapshot
                </h3>
              </div>
              {snapshotRows.map(({ icon: Icon, label, value, sub }) => (
                <div
                  key={label}
                  className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-b-0"
                >
                  <div className="w-7 h-7 rounded-[7px] bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-[0.04em]">
                      {label}
                    </div>
                    <div className="text-[12.5px] text-foreground font-semibold mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                      {value}
                      {sub && (
                        <span className="text-muted-foreground font-normal ml-1">
                          {sub}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Skills */}
            {skills?.length > 0 && (
              <div
                ref={(el) => {
                  sectionRefs.current.skills = el;
                }}
                id="cp-section-skills"
                className="bg-card border border-border rounded-xl p-[16px_18px]"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                    <Award size={12} />
                  </div>
                  <h3 className="text-[13.5px] font-bold tracking-[-0.02em] text-foreground m-0">
                    Skills
                  </h3>
                  <span className="font-mono text-[10.5px] font-semibold text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded-full ml-auto">
                    {skillsCount}
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {(hasSkillTypes
                    ? [
                        { group: domainSkills, title: 'Domain' },
                        { group: toolSkills, title: 'Tools' },
                        { group: regSkills, title: 'Regulatory' },
                      ]
                    : [{ group: skills, title: null }]
                  )
                    .filter(({ group }) => group.length > 0)
                    .map(({ group, title }) => (
                      <div key={title || 'all'}>
                        {title && (
                          <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                            {title}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {group.map((sk) => {
                            const skName =
                              typeof sk === 'string'
                                ? sk
                                : sk?.skill_name || sk?.name || '';
                            const skKey =
                              typeof sk === 'object' && sk?.id
                                ? String(sk.id)
                                : skName;
                            return <SkillPill key={skKey} label={skName} />;
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {certifications?.length > 0 && (
              <div
                ref={(el) => {
                  sectionRefs.current.certifications = el;
                }}
                id="cp-section-certs"
                className="bg-card border border-border rounded-xl p-[16px_18px]"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-[6px] bg-ds-success-subtle text-ds-success border border-ds-success/20 flex items-center justify-center flex-shrink-0">
                    <Award size={12} />
                  </div>
                  <h3 className="text-[13.5px] font-bold tracking-[-0.02em] text-foreground m-0">
                    Certifications
                  </h3>
                  <span className="font-mono text-[10.5px] font-semibold text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded-full ml-auto">
                    {certifications.length}
                  </span>
                </div>
                <div>
                  {certifications.map((cert) => {
                    const certName =
                      typeof cert === 'string'
                        ? cert
                        : cert?.certification_name || cert?.name || '';
                    const certBody =
                      typeof cert === 'object' ? cert?.issuing_body : null;
                    const certCat =
                      typeof cert === 'object' ? cert?.category : null;
                    const certKey =
                      typeof cert === 'object' && cert?.id
                        ? String(cert.id)
                        : certName;
                    return (
                      <div
                        key={certKey}
                        className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-b-0"
                      >
                        <div className="w-8 h-8 rounded-[7px] bg-ds-success-subtle text-ds-success border border-ds-success/20 flex items-center justify-center flex-shrink-0">
                          <Award size={13} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-[650] text-foreground">
                            {certName}
                          </div>
                          {certBody && (
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {certBody}
                            </div>
                          )}
                          {certCat && (
                            <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-[0.04em] text-primary bg-brand-bg-subtle px-1.5 py-0.5 rounded-[4px]">
                              {certCat}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        className={`cp-scrim${openCls ? ' cp-open' : ''}`}
        onClick={onClose}
        role="presentation"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Candidate profile"
        className={`cp-modal${mounted ? ' cp-mounted' : ''}${
          openCls ? ' cp-open' : ''
        }`}
      >
        {/* Loading bar */}
        <div className={`cp-load${isLoading ? ' cp-on' : ''}`} />

        {/* ── Header ── */}
        <div className="cp-bar">
          <div className="cp-bar-av">{headerInitials || '?'}</div>
          <div className="cp-bar-txt">
            <div className="cp-bar-name">{headerName || '—'}</div>
            {headerSub && <div className="cp-bar-sub">{headerSub}</div>}
          </div>

          {candidateScore != null && (
            <div className={`cp-bar-score ${tierCls}`}>
              <span className="cp-score-num">{Math.round(candidateScore)}</span>
              <span className="cp-score-denom">/100</span>
            </div>
          )}

          {/* Pager */}
          {(candidates?.length ?? 0) > 1 && (
            <div className="cp-pager">
              <button
                type="button"
                className="cp-pg"
                disabled={!hasPrev}
                onClick={() => onNavigate && onNavigate(currentIndex - 1)}
                aria-label="Previous candidate"
              >
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <span className="cp-pg-count">
                {currentIndex + 1} of {candidates.length}
              </span>
              <button
                type="button"
                className="cp-pg"
                disabled={!hasNext}
                onClick={() => onNavigate && onNavigate(currentIndex + 1)}
                aria-label="Next candidate"
              >
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          )}

          <a
            href={fullPageHref}
            target="_blank"
            rel="noopener noreferrer"
            className="cp-btn-ghost"
          >
            <ExternalLink size={13} strokeWidth={1.8} />
            Full page
          </a>

          <button
            type="button"
            className="cp-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={13} strokeWidth={2} />
            Close
            <span className="cp-esc-badge">Esc</span>
          </button>
        </div>

        {/* ── Sub-nav ── */}
        <nav className="cp-subnav" aria-label="Profile sections">
          {[
            { key: 'match', label: 'Match evidence' },
            { key: 'experience', label: 'Experience' },
            { key: 'skills', label: 'Skills' },
            { key: 'education', label: 'Education' },
            { key: 'certifications', label: 'Certifications' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`cp-nav-btn${activeSection === key ? ' cp-active' : ''}`}
              onClick={() => scrollToSection(key)}
            >
              {label}
            </button>
          ))}
          <span className="cp-subnav-hint">
            &#8592;&nbsp;&#8594; navigate
            {onToggleSelect && <> &middot; S shortlist</>} &middot; Esc close
          </span>
        </nav>

        {/* ── Scrollable body ── */}
        <div className="cp-scroll" ref={scrollRef}>
          {renderBody()}
        </div>

        {/* ── Footer ── */}
        <div className="cp-foot">
          <span className="cp-foot-meta">
            {candidates?.length
              ? `${currentIndex + 1} of ${candidates.length} candidates`
              : null}
          </span>
          <div className="cp-foot-actions">
            {onToggleSelect && (
              <button
                type="button"
                className={`cp-foot-btn ${isSelected ? 'cp-shortlisted' : 'cp-shortlist'}`}
                onClick={() => onToggleSelect(candidateId)}
                disabled={!isSelected && selectDisabled}
              >
                {isSelected && <Check size={14} strokeWidth={2.5} />}
                {isSelected ? 'Shortlisted' : 'Select to shortlist'}
              </button>
            )}
            <button
              type="button"
              className="cp-foot-btn cp-ghost"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateProfileModal;
